import { useMemo, useState, useEffect } from "react";
import dayjs, { Dayjs } from "dayjs";
import {
  Box,
  Tabs,
  Tab,
  Stack,
  TextField,
  Button,
  Card,
  CardContent,
  Typography,
  Chip,
  Divider,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Tooltip,
  Paper,
  Alert,
  LinearProgress,
  Autocomplete,
  Pagination,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { useTranslation } from "react-i18next";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RTooltip,
  Legend,
  BarChart,
  Bar,
} from "recharts";
import { useQuery } from "@tanstack/react-query";
import api from "../../../api/axios";

import { useClientes } from "../../../hooks/useClientes";
import { useProfesores } from "../../../hooks/useProfesores";
import { useProyectos } from "../../../hooks/useProyectos";

import { getEvaluacionesDeReserva } from "../../../api/evaluaciones";
import { getReservaAlumnosIds } from "../../../api/reservas-alumnos";
import {
  useEvaluacionesSearch,
  useEvaluacionesResumen,
  useFirstClassDate,
} from "../../../hooks/useEvaluacionesSearch";
import { getReserva } from "../../../api/reversas";

// ===== Helper mini-hooks para catálogos (si ya los tienes, puedes reemplazarlos) =========
type Asignatura = {
  id: number;
  nombre_asignatura?: string;
  nombre?: string;
  nombreAsignatura?: string;
};
type Centro = {
  id: number;
  nombre_centro?: string;
  nombre?: string;
  nombreCentro?: string;
};

function useAsignaturas() {
  const { data = [], isFetching } = useQuery({
    queryKey: ["asignaturas"],
    queryFn: async () => {
      const { data } = await api.get<Asignatura[]>("/asignaturas");
      return Array.isArray(data) ? data : [];
    },
    staleTime: 5 * 60_000,
  });
  return {
    rows: data,
    loading: isFetching,
    getName: (id?: number) => {
      const x = data.find((a) => a.id === id);
      return x?.nombre_asignatura || x?.nombre || x?.nombreAsignatura || (id ? `#${id}` : "");
    },
  };
}

function useCentros() {
  const { data = [], isFetching } = useQuery({
    queryKey: ["centros"],
    queryFn: async () => {
      const { data } = await api.get<Centro[]>("/centros");
      return Array.isArray(data) ? data : [];
    },
    staleTime: 5 * 60_000,
  });
  return {
    rows: data,
    loading: isFetching,
    getName: (id?: number | null) => {
      const x = data.find((c) => c.id === id);
      return x?.nombre_centro || x?.nombre || x?.nombreCentro || (id ? `#${id}` : "—");
    },
  };
}

// ===== Tipos de la búsqueda (para referencia)
type EvaluacionRow = {
  reservaId: number;
  fecha: string; // YYYY-MM-DD
  alumnoId: number;
  profesorId: number;
  asignaturaId: number;
  centroId: number | null;
  proyecto: string;
  puntualidad?: number;
  motivacion?: number;
  atencion?: number;
  deberes?: number;
  nivel?: number;
  media?: number;
  observaciones?: string | null;
};

// ===== Utils
function fmt(d?: string) {
  return d ? dayjs(d).format("YYYY-MM-DD") : "";
}

// =========================================================================================
//                                      PAGE
// =========================================================================================
export default function EvaluacionesPage() {
  const { t } = useTranslation<"translation">();
  const [tab, setTab] = useState<"reserva" | "alumno" | "dashboard">("reserva");

  return (
    <Box p={2}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        {t("informes.evaluaciones.title", "Evaluaciones")}
      </Typography>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label={t("informes.evaluaciones.tabs.byBooking", "Por reserva")} value="reserva" />
        <Tab
          label={t("informes.evaluaciones.tabs.byStudent", "Histórico por alumno")}
          value="alumno"
        />
        <Tab label={t("informes.evaluaciones.tabs.dashboard", "Dashboard")} value="dashboard" />
      </Tabs>

      {tab === "reserva" && <TabPorReserva />}
      {tab === "alumno" && <TabHistoricoAlumno />}
      {tab === "dashboard" && <TabDashboard />}
    </Box>
  );
}

// =========================================================================================
//                              TAB 1: POR RESERVA
// =========================================================================================
function TabPorReserva() {
  const { t } = useTranslation<"translation">();
  const { rows: clientes } = useClientes();
  const { rows: profesores } = useProfesores();
  const { getName: asignaturaName } = useAsignaturas();
  const { getName: centroName } = useCentros();
  const { rows: proyectos = [] } = useProyectos(); // ⬅️ para mostrar nombre si sólo llega id

  const proyNameById = useMemo(() => {
    const m = new Map<number, string>();
    for (const p of proyectos ?? []) {
      if (!p || typeof p.id !== "number") continue;
      m.set(p.id, p.nombre ?? `#${p.id}`);
    }
    return (id?: number | null) => (typeof id === "number" ? (m.get(id) ?? `#${id}`) : "");
  }, [proyectos]);

  const [inputId, setInputId] = useState<string>("");
  const [reservaId, setReservaId] = useState<number | null>(null);

  const {
    data: evalData,
    isFetching,
    error,
  } = useQuery({
    queryKey: ["eval-by-reserva", reservaId],
    enabled: !!reservaId,
    queryFn: () => getEvaluacionesDeReserva(reservaId!),
    retry: 0,
  });

  // Detalle de la reserva para completar fecha/asignatura/proyecto/profesor
  const { data: reserva } = useQuery({
    queryKey: ["reserva-detalle", reservaId],
    enabled: !!reservaId,
    queryFn: () => getReserva(reservaId!),
  });

  const { data: alumnosIds = [] } = useQuery({
    queryKey: ["reserva-alumnos-ids", reservaId],
    enabled: !!reservaId,
    queryFn: () => getReservaAlumnosIds(reservaId!),
  });

  const nombreAlumno = useMemo(() => {
    const m = new Map<number, string>();
    for (const c of clientes ?? []) {
      if (!c || typeof c.id !== "number") continue;
      m.set(c.id, c.nombreCompleto ?? c.email ?? `#${c.id}`);
    }
    return (id: number) => m.get(id) ?? `#${id}`;
  }, [clientes]);

  const nombreProfesor = useMemo(() => {
    const m = new Map<number, string>();
    for (const p of profesores ?? []) {
      if (!p || typeof p.id !== "number") continue;
      m.set(p.id, p.nombreCompleto ?? (p as any)?.nombre ?? p.email ?? `#${p.id}`);
    }
    return (id: number) => m.get(id) ?? `#${id}`;
  }, [profesores]);

  const pendientes = useMemo(() => {
    if (!evalData?.items || !alumnosIds?.length) return [];
    const evaluados = new Set(evalData.items.map((i) => i.alumnoId));
    return alumnosIds.filter((id) => !evaluados.has(id));
  }, [evalData, alumnosIds]);

  // Filas completas para la tabla
  const rowsTabla: EvaluacionRow[] = useMemo(() => {
    if (!evalData?.items?.length) return [];

    const profId = evalData.profesorId ?? reserva?.idProfesor ?? 0;
    const asignId = reserva?.idAsignatura ?? 0;
    // const centroId = (reserva as any)?.idCentro ?? null; // si tu backend ya lo devuelve
    const centroId = null;
    const proyectoStr =
      (reserva as any)?.nombreProyecto ??
      (reserva as any)?.proyecto ??
      proyNameById((reserva as any)?.idProyecto ?? null) ??
      "";

    const fechaStr = (reserva as any)?.fecha ?? "";

    return evalData.items.map((it) => ({
      reservaId: reservaId!,
      fecha: fechaStr ? fmt(String(fechaStr)) : "",
      alumnoId: it.alumnoId,
      profesorId: profId,
      asignaturaId: asignId,
      centroId,
      proyecto: proyectoStr,
      puntualidad: it.puntualidad,
      motivacion: it.motivacion,
      atencion: it.atencion,
      deberes: it.deberes,
      nivel: it.nivel,
      media: (it.puntualidad + it.motivacion + it.atencion + it.deberes + it.nivel) / 5,
      observaciones: it.observaciones ?? null,
    }));
  }, [evalData, reserva, reservaId, proyNameById]);

  return (
    <Box>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction="row" spacing={2} alignItems="center">
            <TextField
              label={t("Reserva ID", "Reserva ID")}
              value={inputId}
              onChange={(e) => setInputId(e.target.value.replace(/[^\d]/g, ""))}
              size="small"
            />
            <Button
              variant="contained"
              onClick={() => setReservaId(inputId ? Number(inputId) : null)}
              disabled={!inputId}
            >
              {t("Buscar", "Buscar")}
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {isFetching && <LinearProgress />}

      {!isFetching && error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {(error as any)?.message ?? "Error"}
        </Alert>
      )}

      {!isFetching && reservaId && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            {t("Evaluaciones de la reserva", "Evaluaciones de la reserva")} #{reservaId}
          </Typography>

          {rowsTabla.length ? (
            <TablaEvaluaciones
              rows={rowsTabla}
              nombreAlumno={nombreAlumno}
              nombreProfesor={nombreProfesor}
              nombreAsignatura={(id) => asignaturaName(id)}
              nombreCentro={(id) => centroName(id)}
            />
          ) : (
            <Alert severity="info" sx={{ mb: 2 }}>
              {t(
                "No hay evaluaciones registradas para esta reserva.",
                "No hay evaluaciones registradas para esta reserva."
              )}
            </Alert>
          )}

          {!!pendientes.length && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2">
                {t("Pendientes de evaluar", "Pendientes de evaluar")}
              </Typography>
              <Stack direction="row" gap={1} flexWrap="wrap" sx={{ mt: 1 }}>
                {pendientes.map((id) => (
                  <Chip key={id} label={nombreAlumno(id)} />
                ))}
              </Stack>
            </>
          )}
        </Paper>
      )}
    </Box>
  );
}

// =========================================================================================
//                         TAB 2: HISTÓRICO POR ALUMNO
// =========================================================================================
function TabHistoricoAlumno() {
  const { t } = useTranslation<"translation">();
  const { rows: clientes } = useClientes();
  const { rows: profesores } = useProfesores();
  const { getName: asignaturaName } = useAsignaturas();
  const { rows: proyectos = [] } = useProyectos(); // ⬅️ para mapear id→nombre

  const proyNameById = useMemo(() => {
    const m = new Map<number, string>();
    for (const p of proyectos ?? []) {
      if (!p || typeof p.id !== "number") continue;
      m.set(p.id, p.nombre ?? `#${p.id}`);
    }
    return (id?: number | null) => (typeof id === "number" ? (m.get(id) ?? `#${id}`) : "");
  }, [proyectos]);

  const [alumnoId, setAlumnoId] = useState<number | null>(null);
  const { data: firstDateStr } = useFirstClassDate(alumnoId);

  const [desde, setDesde] = useState<Dayjs | null>(null);
  const [hasta, setHasta] = useState<Dayjs | null>(dayjs());

  useEffect(() => {
    if (firstDateStr) setDesde(dayjs(firstDateStr));
  }, [firstDateStr]);

  const params = useMemo(
    () => ({
      alumnoIds: alumnoId ? [alumnoId] : undefined,
      desde: desde ? dayjs(desde).format("YYYY-MM-DD") : undefined,
      hasta: hasta ? dayjs(hasta).format("YYYY-MM-DD") : undefined,
      page: 1,
      pageSize: 1000,
    }),
    [alumnoId, desde, hasta]
  );

  const { data, isFetching } = useEvaluacionesSearch(params);

  const nombreAlumno = useMemo(() => {
    const m = new Map<number, string>();
    for (const c of clientes ?? []) {
      if (!c || typeof c.id !== "number") continue;
      m.set(c.id, c.nombreCompleto ?? c.email ?? `#${c.id}`);
    }
    return (id: number) => m.get(id) ?? `#${id}`;
  }, [clientes]);

  const nombreProfesor = useMemo(() => {
    const m = new Map<number, string>();
    for (const p of profesores ?? []) {
      if (!p || typeof p.id !== "number") continue;
      m.set(p.id, p.nombreCompleto ?? (p as any)?.nombre ?? p.email ?? `#${p.id}`);
    }
    return (id: number) => m.get(id) ?? `#${id}`;
  }, [profesores]);

  // Sólo el alumno seleccionado (y mapeamos proyecto si viene por id)
  const rowsAlumno = useMemo(() => {
    const all = (data?.rows ?? []) as any[];
    return alumnoId
      ? all
          .filter((r) => r.alumnoId === alumnoId)
          .map((r) => ({
            ...r,
            proyecto: r.proyecto ?? r.nombreProyecto ?? proyNameById(r.idProyecto ?? null) ?? "—",
          }))
      : [];
  }, [data, alumnoId, proyNameById]);

  // Serie para la gráfica
  const series = useMemo(() => {
    const toNum = (v: any) => (v === null || v === undefined || v === "" ? null : Number(v));
    const rows = rowsAlumno.slice().sort((a, b) => a.fecha.localeCompare(b.fecha));
    return rows.map((r) => ({
      date: r.fecha,
      puntualidad: toNum(r.puntualidad),
      motivacion: toNum(r.motivacion),
      atencion: toNum(r.atencion),
      deberes: toNum(r.deberes),
      nivel: toNum(r.nivel),
      media: toNum(r.media),
    }));
  }, [rowsAlumno]);

  const lineColors = {
    puntualidad: "#1e88e5",
    motivacion: "#8e24aa",
    atencion: "#43a047",
    deberes: "#fb8c00",
    nivel: "#e53935",
    media: "#546e7a",
  };

  return (
    <Box>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <Autocomplete
              options={clientes}
              getOptionLabel={(o) => o?.nombreCompleto || o?.email || ""}
              onChange={(_, v) => setAlumnoId(v?.id ?? null)}
              renderInput={(params) => (
                <TextField {...params} label={t("Alumno", "Alumno")} size="small" />
              )}
              sx={{ minWidth: 280 }}
            />
            <DatePicker
              label={t("Desde", "Desde")}
              value={desde}
              onChange={setDesde}
              slotProps={{ textField: { size: "small" } }}
            />
            <DatePicker
              label={t("Hasta", "Hasta")}
              value={hasta}
              onChange={setHasta}
              slotProps={{ textField: { size: "small" } }}
            />
          </Stack>
        </CardContent>
      </Card>

      {isFetching && <LinearProgress />}

      {alumnoId && (
        <>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              {t("Progresión del alumno", "Progresión del alumno")} · {nombreAlumno(alumnoId)}
            </Typography>

            <Box sx={{ height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={series}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tickFormatter={(d) => dayjs(d).format("YYYY-MM-DD")} />
                  <YAxis domain={[1, 5]} ticks={[1, 2, 3, 4, 5]} />
                  <RTooltip labelFormatter={(d) => dayjs(d).format("YYYY-MM-DD")} />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="puntualidad"
                    name="Puntualidad"
                    stroke={lineColors.puntualidad}
                    dot={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="motivacion"
                    name="Motivación"
                    stroke={lineColors.motivacion}
                    dot={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="atencion"
                    name="Atención"
                    stroke={lineColors.atencion}
                    dot={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="deberes"
                    name="Deberes"
                    stroke={lineColors.deberes}
                    dot={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="nivel"
                    name="Nivel"
                    stroke={lineColors.nivel}
                    dot={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="media"
                    name="Media"
                    stroke={lineColors.media}
                    strokeDasharray="4 2"
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </Paper>

          <Paper sx={{ p: 2 }}>
            <TablaEvaluaciones
              rows={rowsAlumno}
              nombreAlumno={nombreAlumno}
              nombreProfesor={nombreProfesor}
              nombreAsignatura={(id) => asignaturaName(id)}
              nombreCentro={() => "—"}
            />
          </Paper>

          {!rowsAlumno.length && !isFetching && (
            <Alert severity="info" sx={{ mt: 2 }}>
              {t(
                "No hay evaluaciones para este alumno en el rango seleccionado.",
                "No hay evaluaciones para este alumno en el rango seleccionado."
              )}
            </Alert>
          )}
        </>
      )}
    </Box>
  );
}

// =========================================================================================
//                              TAB 3: DASHBOARD GLOBAL
// =========================================================================================
function TabDashboard() {
  const { t } = useTranslation<"translation">();
  const { rows: clientes } = useClientes();
  const { rows: profesores } = useProfesores();
  const { rows: asignaturas, getName: asignaturaName } = useAsignaturas();
  const { rows: centros, getName: centroName } = useCentros();
  const { rows: proyectos = [] } = useProyectos(); // ⬅️ proyectos dinámicos

  const proyNameById = useMemo(() => {
    const m = new Map<number, string>();
    for (const p of proyectos ?? []) {
      if (!p || typeof p.id !== "number") continue;
      m.set(p.id, p.nombre ?? `#${p.id}`);
    }
    return (id?: number | null) => (typeof id === "number" ? (m.get(id) ?? `#${id}`) : "");
  }, [proyectos]);

  const [desde, setDesde] = useState<Dayjs | null>(dayjs().subtract(90, "day"));
  const [hasta, setHasta] = useState<Dayjs | null>(dayjs());
  const [selAlumnos, setSelAlumnos] = useState<number[]>([]);
  const [selProfes, setSelProfes] = useState<number[]>([]);
  const [selAsigs, setSelAsigs] = useState<number[]>([]);
  const [selCentros, setSelCentros] = useState<number[]>([]);
  const [selProyectos, setSelProyectos] = useState<number[]>([]); // ← IDs
  const [scoreMin, setScoreMin] = useState<number | "">("");
  const [scoreMax, setScoreMax] = useState<number | "">("");
  const [sinEval, setSinEval] = useState(false);
  const [page, setPage] = useState(1);

  const baseParams = useMemo(() => {
    // Normaliza fechas
    const pDesde = desde ? fmt(desde.format("YYYY-MM-DD")) : undefined;
    const pHasta = hasta ? fmt(hasta.format("YYYY-MM-DD")) : undefined;

    // Normaliza scores a enteros 1..5 y corrige si min>max
    const minVal =
      typeof scoreMin === "number" && Number.isFinite(scoreMin)
        ? Math.max(1, Math.min(5, Math.round(scoreMin)))
        : undefined;
    const maxVal =
      typeof scoreMax === "number" && Number.isFinite(scoreMax)
        ? Math.max(1, Math.min(5, Math.round(scoreMax)))
        : undefined;

    const sMin = minVal !== undefined && maxVal !== undefined && minVal > maxVal ? maxVal : minVal;
    const sMax = minVal !== undefined && maxVal !== undefined && minVal > maxVal ? minVal : maxVal;

    return {
      desde: pDesde,
      hasta: pHasta,
      alumnoIds: selAlumnos.length ? selAlumnos : undefined,
      profesorIds: selProfes.length ? selProfes : undefined,
      asignaturaIds: selAsigs.length ? selAsigs : undefined,
      centroIds: selCentros.length ? selCentros : undefined,
      proyectoIds: selProyectos.length ? selProyectos : undefined,
      scoreMin: sMin,
      scoreMax: sMax,
    };
  }, [desde, hasta, selAlumnos, selProfes, selAsigs, selCentros, selProyectos, scoreMin, scoreMax]);

  const { data: evalSearch, isFetching } = useEvaluacionesSearch({
    ...baseParams,
    sinEvaluacion: sinEval || undefined,
    page,
    pageSize: 50,
  });

  const { data: resMes } = useEvaluacionesResumen({
    ...baseParams,
    groupBy: "mes",
  });
  const { data: resAsig } = useEvaluacionesResumen({
    ...baseParams,
    groupBy: "asignatura",
  });
  const { data: resCentro } = useEvaluacionesResumen({
    ...baseParams,
    groupBy: "centro",
  });

  const kpi = useMemo(() => {
    const rows = resMes?.rows ?? [];
    const totalN = rows.reduce((s: number, r: any) => s + Number(r.n || 0), 0);
    const mediaW = totalN
      ? rows.reduce((s: number, r: any) => s + Number(r.media || 0) * Number(r.n || 0), 0) / totalN
      : null;
    return { totalN, mediaW };
  }, [resMes]);

  const { data: pendientesData } = useEvaluacionesSearch({
    ...baseParams,
    sinEvaluacion: true,
    page: 1,
    pageSize: 1,
  });
  const pendientesTotal = pendientesData?.total ?? 0;

  const nombreAlumno = useMemo(() => {
    const m = new Map<number, string>();
    for (const c of clientes ?? []) {
      if (!c || typeof c.id !== "number") continue;
      m.set(c.id, c.nombreCompleto ?? c.email ?? `#${c.id}`);
    }
    return (id: number) => m.get(id) ?? `#${id}`;
  }, [clientes]);

  const nombreProfesor = useMemo(() => {
    const m = new Map<number, string>();
    for (const p of profesores ?? []) {
      if (!p || typeof p.id !== "number") continue;
      m.set(p.id, p.nombreCompleto ?? (p as any)?.nombre ?? p.email ?? `#${p.id}`);
    }
    return (id: number) => m.get(id) ?? `#${id}`;
  }, [profesores]);

  // Normaliza el nombre de proyecto en las filas para la tabla
  const rowsTabla = useMemo(() => {
    const rows = (evalSearch?.rows ?? []) as any[];
    return rows.map((r) => ({
      ...r,
      proyecto: r.proyecto ?? r.nombreProyecto ?? proyNameById(r.idProyecto ?? null) ?? "—",
    }));
  }, [evalSearch, proyNameById]);

  return (
    <Box>
      {/* Filtros */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems="flex-start">
            <DatePicker
              label={t("Desde", "Desde")}
              value={desde}
              onChange={(v) => {
                setDesde(v);
                setPage(1);
              }}
              slotProps={{ textField: { size: "small" } }}
            />
            <DatePicker
              label={t("Hasta", "Hasta")}
              value={hasta}
              onChange={(v) => {
                setHasta(v);
                setPage(1);
              }}
              slotProps={{ textField: { size: "small" } }}
            />

            <Autocomplete
              multiple
              options={clientes}
              getOptionLabel={(o) => o?.nombreCompleto || o?.email || ""}
              onChange={(_, v) => {
                setSelAlumnos(v.map((x) => x.id));
                setPage(1);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("Alumno", "Alumno")}
                  size="small"
                  sx={{ minWidth: 240 }}
                />
              )}
            />

            <Autocomplete
              multiple
              options={profesores}
              getOptionLabel={(o) => o?.nombreCompleto || o?.email || ""}
              onChange={(_, v) => {
                setSelProfes(v.map((x) => x.id));
                setPage(1);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("Profesor", "Profesor")}
                  size="small"
                  sx={{ minWidth: 240 }}
                />
              )}
            />
          </Stack>

          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems="flex-start"
            sx={{ mt: 2 }}
          >
            <Autocomplete
              multiple
              options={asignaturas}
              getOptionLabel={(o) =>
                o?.nombre_asignatura || o?.nombre || o?.nombreAsignatura || `#${o?.id ?? ""}`
              }
              onChange={(_, v) => {
                setSelAsigs(v.map((x) => x.id));
                setPage(1);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("Asignatura", "Asignatura")}
                  size="small"
                  sx={{ minWidth: 240 }}
                />
              )}
            />

            <Autocomplete
              multiple
              options={centros}
              getOptionLabel={(o) =>
                o?.nombre_centro || o?.nombre || o?.nombreCentro || `#${o?.id ?? ""}`
              }
              onChange={(_, v) => {
                setSelCentros(v.map((x) => x.id));
                setPage(1);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("Centro", "Centro")}
                  size="small"
                  sx={{ minWidth: 240 }}
                />
              )}
            />

            {/* Proyecto (IDs dinámicos) */}
            <Autocomplete
              multiple
              options={proyectos}
              getOptionLabel={(o) => o?.nombre || `#${o?.id ?? ""}`}
              value={proyectos.filter((p) => selProyectos.includes(p.id))}
              onChange={(_, v) => {
                setSelProyectos(v.map((x) => x.id));
                setPage(1);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("Proyecto", "Proyecto")}
                  size="small"
                  sx={{ minWidth: 240 }}
                />
              )}
            />

            <TextField
              label={t("Min avg", "Min avg")}
              size="small"
              type="number"
              value={scoreMin}
              onChange={(e) => {
                const v = e.target.value;
                if (v === "") {
                  setScoreMin("");
                } else {
                  const n = Math.max(1, Math.min(5, Math.round(Number(v))));
                  setScoreMin(Number.isFinite(n) ? n : "");
                }
                setPage(1);
              }}
              inputProps={{ min: 1, max: 5, step: 1 }}
              sx={{ width: 110 }}
            />
            <TextField
              label={t("Max avg", "Max avg")}
              size="small"
              type="number"
              value={scoreMax}
              onChange={(e) => {
                const v = e.target.value;
                if (v === "") {
                  setScoreMax("");
                } else {
                  const n = Math.max(1, Math.min(5, Math.round(Number(v))));
                  setScoreMax(Number.isFinite(n) ? n : "");
                }
                setPage(1);
              }}
              inputProps={{ min: 1, max: 5, step: 1 }}
              sx={{ width: 110 }}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={sinEval}
                  onChange={(e) => {
                    setSinEval(e.target.checked);
                    setPage(1);
                  }}
                />
              }
              label={t("Mostrar sin evaluación", "Mostrar sin evaluación")}
            />
          </Stack>
        </CardContent>
      </Card>

      {/* KPIs */}
      <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mb: 2 }}>
        <Paper sx={{ p: 2, flex: 1 }}>
          <Typography variant="overline">
            {t("informes.evaluaciones.kpi.total", "Total evaluaciones")}
          </Typography>
          <Typography variant="h5">
            {resMes?.rows?.reduce((s: number, r: any) => s + Number(r.n || 0), 0) ?? 0}
          </Typography>
        </Paper>
        <Paper sx={{ p: 2, flex: 1 }}>
          <Typography variant="overline">
            {t("informes.evaluaciones.kpi.avg", "Media global")}
          </Typography>
          <Typography variant="h5">{kpi.mediaW ? kpi.mediaW.toFixed(2) : "—"}</Typography>
        </Paper>
        <Paper sx={{ p: 2, flex: 1 }}>
          <Typography variant="overline">
            {t("informes.evaluaciones.kpi.missing", "Pendientes de evaluación")}
          </Typography>
          <Typography variant="h5">{pendientesTotal}</Typography>
        </Paper>
      </Stack>

      {/* Gráficos */}
      <Stack direction={{ xs: "column", lg: "row" }} spacing={2} sx={{ mb: 2 }}>
        <Paper sx={{ p: 2, flex: 1 }}>
          <Typography variant="subtitle1" gutterBottom>
            {t("Media por asignatura", "Media por asignatura")}
          </Typography>
          <Box sx={{ height: 280 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={(resAsig?.rows ?? []).map((r) => ({
                  name: asignaturaName(Number(r.grp)),
                  media: Number(r.media || 0),
                }))}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis domain={[1, 5]} />
                <RTooltip />
                <Bar dataKey="media" name="Media" />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Paper>

        <Paper sx={{ p: 2, flex: 1 }}>
          <Typography variant="subtitle1" gutterBottom>
            {t("Media por centro", "Media por centro")}
          </Typography>
          <Box sx={{ height: 280 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={(resCentro?.rows ?? []).map((r) => ({
                  name: centroName(Number(r.grp)),
                  media: Number(r.media || 0),
                }))}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis domain={[1, 5]} />
                <RTooltip />
                <Bar dataKey="media" name="Media" />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Paper>
      </Stack>

      {/* Tabla paginada */}
      <Paper sx={{ p: 2 }}>
        {isFetching && <LinearProgress sx={{ mb: 2 }} />}
        <TablaEvaluaciones
          rows={rowsTabla}
          nombreAlumno={(id) => {
            const c = clientes.find((x) => x?.id === id);
            return c?.nombreCompleto || c?.email || `#${id}`;
          }}
          nombreProfesor={(id) => {
            const p = profesores.find((x) => x?.id === id);
            return p?.nombreCompleto ?? (p as any)?.nombre ?? p?.email ?? `#${id}`;
          }}
          nombreAsignatura={(id) => asignaturaName(id)}
          nombreCentro={(id) => {
            const c = centros.find((x) => x?.id === id);
            return c?.nombre_centro || c?.nombre || (typeof id === "number" ? `#${id}` : "—");
          }}
        />

        {!!evalSearch?.total && (
          <Stack alignItems="center" sx={{ mt: 2 }}>
            <Pagination
              page={page}
              onChange={(_, p) => setPage(p)}
              count={Math.max(1, Math.ceil((evalSearch?.total ?? 0) / 50))}
              color="primary"
            />
          </Stack>
        )}
      </Paper>
    </Box>
  );
}

// =========================================================================================
//                                   Tabla común
// =========================================================================================
function TablaEvaluaciones({
  rows,
  nombreAlumno,
  nombreProfesor,
  nombreAsignatura,
  nombreCentro,
}: {
  rows: any[];
  nombreAlumno: (id: number) => string;
  nombreProfesor: (id: number) => string;
  nombreAsignatura: (id?: number) => string;
  nombreCentro: (id?: number | null) => string;
}) {
  const { t } = useTranslation<"translation">();

  const toNum = (v: unknown): number | undefined => {
    if (v === null || v === undefined || v === "") return undefined;
    const n = typeof v === "number" ? v : Number(v);
    return Number.isFinite(n) ? n : undefined;
  };

  const scoreColor = (v: unknown) => {
    const n = toNum(v);
    if (n === undefined) return "default" as const;
    if (n <= 2) return "error" as const;
    if (n === 3) return "warning" as const;
    return "success" as const;
  };

  const fmt = (d?: string) => (d ? dayjs(d).format("YYYY-MM-DD") : "");

  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>{t("informes.evaluaciones.table.date", "Fecha")}</TableCell>
          <TableCell>{t("informes.evaluaciones.table.student", "Alumno")}</TableCell>
          <TableCell>{t("informes.evaluaciones.table.teacher", "Profesor")}</TableCell>
          <TableCell>{t("informes.evaluaciones.table.subject", "Asignatura")}</TableCell>
          <TableCell>{t("informes.evaluaciones.table.project", "Proyecto")}</TableCell>
          <TableCell align="center">
            {t("informes.evaluaciones.table.punctuality", "Punctuality")}
          </TableCell>
          <TableCell align="center">
            {t("informes.evaluaciones.table.motivation", "Motivation")}
          </TableCell>
          <TableCell align="center">
            {t("informes.evaluaciones.table.attention", "Attention")}
          </TableCell>
          <TableCell align="center">
            {t("informes.evaluaciones.table.homework", "Homework")}
          </TableCell>
          <TableCell align="center">{t("informes.evaluaciones.table.level", "Level")}</TableCell>
          <TableCell align="center">{t("informes.evaluaciones.table.avg", "Average")}</TableCell>
          <TableCell>{t("informes.evaluaciones.table.notes", "Notes")}</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {(rows ?? []).map((r, idx) => {
          const p = toNum(r.puntualidad);
          const m = toNum(r.motivacion);
          const a = toNum(r.atencion);
          const d = toNum(r.deberes);
          const n = toNum(r.nivel);
          const mg = toNum(r.media);

          return (
            <TableRow key={`${r.reservaId}-${r.alumnoId}-${idx}`}>
              <TableCell>{fmt(r.fecha)}</TableCell>
              <TableCell>{nombreAlumno(r.alumnoId)}</TableCell>
              <TableCell>{nombreProfesor(r.profesorId)}</TableCell>
              <TableCell>{nombreAsignatura(r.asignaturaId)}</TableCell>
              <TableCell>{r.proyecto || "—"}</TableCell>

              <TableCell align="center">
                <Chip size="small" color={scoreColor(p)} label={p ?? "—"} />
              </TableCell>
              <TableCell align="center">
                <Chip size="small" color={scoreColor(m)} label={m ?? "—"} />
              </TableCell>
              <TableCell align="center">
                <Chip size="small" color={scoreColor(a)} label={a ?? "—"} />
              </TableCell>
              <TableCell align="center">
                <Chip size="small" color={scoreColor(d)} label={d ?? "—"} />
              </TableCell>
              <TableCell align="center">
                <Chip size="small" color={scoreColor(n)} label={n ?? "—"} />
              </TableCell>

              <TableCell align="center">
                <Chip
                  size="small"
                  color={scoreColor(mg)}
                  label={mg !== undefined ? mg.toFixed(2) : "—"}
                />
              </TableCell>

              <TableCell>
                {r.observaciones ? (
                  <Tooltip title={r.observaciones}>
                    <span>
                      {String(r.observaciones).slice(0, 40)}
                      {String(r.observaciones).length > 40 ? "…" : ""}
                    </span>
                  </Tooltip>
                ) : (
                  "—"
                )}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
